import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { AppsMenu } from "../components/AppsMenu";
import { MainMenu } from "../components/MainMenu";
import api, { API } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";
import { Input } from "../components/ui/input";
import {
  Search, Youtube, Flame, Loader2, PlayCircle, ArrowLeft, ExternalLink, Shield,
} from "lucide-react";

function VideoCard({ v }) {
  return (
    <Link
      to={`/tube/watch?v=${encodeURIComponent(v.id || v.url)}&t=${encodeURIComponent(v.title || "")}`}
      data-testid={`tube-card-${v.id || ""}`}
      className="group"
    >
      <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden relative">
        {v.thumbnail ? (
          <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300"><Youtube size={48} /></div>
        )}
        {v.duration && (
          <span className="absolute right-2 bottom-2 text-xs font-mono px-1.5 py-0.5 rounded bg-black/80 text-white">{v.duration}</span>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition opacity-0 group-hover:opacity-100">
          <PlayCircle className="text-white" size={56} />
        </div>
      </div>
      <h3 className="font-semibold text-sm mt-2 line-clamp-2">{v.title}</h3>
      <div className="text-xs text-gray-500 mt-1">{v.publisher || "Inconnu"}</div>
    </Link>
  );
}

function TubeHeader({ initial, onSubmit }) {
  const [q, setQ] = useState(initial || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQ(initial || ""); }, [initial]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Autocomplete from DDG via /api/search/suggest
  useEffect(() => {
    const txt = q.trim();
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!txt) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/suggest", { params: { q: txt } });
        setSuggestions(data.suggestions || []);
      } catch { setSuggestions([]); }
    }, 150);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const fire = (text) => {
    const final = (text || "").trim();
    if (!final) return;
    setShowDrop(false);
    onSubmit(final);
  };

  const submit = (e) => {
    e.preventDefault();
    if (highlight >= 0 && suggestions[highlight]) fire(suggestions[highlight]);
    else fire(q);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (showDrop && highlight >= 0 && suggestions[highlight]) fire(suggestions[highlight]);
      else fire(q);
      return;
    }
    if (!showDrop || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(suggestions.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(-1, h - 1)); }
    else if (e.key === "Escape") setShowDrop(false);
  };

  return (
    <header className="sticky top-0 z-10 bg-white border-b border-[var(--omni-border)]">
      <div className="px-4 sm:px-6 py-3 flex items-center gap-4">
        <Link to="/tube" className="flex items-center gap-2" data-testid="tube-home-link">
          <Logo size="sm" showWordmark={false} linkTo={null} />
          <span className="font-black text-lg tracking-tight">Omni<span className="text-red-600">Tube</span></span>
        </Link>
        <form ref={wrapRef} onSubmit={submit} className="flex-1 max-w-2xl mx-auto relative" data-testid="tube-search-form">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => { setQ(e.target.value); setShowDrop(true); }}
              onFocus={() => setShowDrop(true)}
              onKeyDown={onKeyDown}
              placeholder="Rechercher des vidéos…"
              className="w-full pl-11 pr-24 py-3 rounded-full border border-[var(--omni-border)] focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
              data-testid="tube-search-input"
              spellCheck="false"
              autoComplete="off"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(""); setSuggestions([]); inputRef.current?.focus(); }}
                className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
                data-testid="tube-clear-btn"
                aria-label="Effacer"
              >
                ×
              </button>
            )}
            <button
              type="submit"
              data-testid="tube-search-submit-btn"
              aria-label="Rechercher"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-red-600 hover:bg-red-700 text-white transition shadow-sm"
            >
              <Search size={15} />
            </button>
          </div>
          {showDrop && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 mt-2 bg-white border border-[var(--omni-border)] rounded-2xl shadow-lg overflow-hidden z-50" data-testid="tube-suggestions">
              {suggestions.map((s, i) => (
                <button
                  key={s + i}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); fire(s); }}
                  onMouseEnter={() => setHighlight(i)}
                  data-testid={`tube-suggestion-${i}`}
                  className={`w-full text-left px-5 py-2.5 text-sm flex items-center gap-3 ${highlight === i ? "bg-[var(--omni-surface)]" : "hover:bg-[var(--omni-surface)]"}`}
                >
                  <Search size={13} className="text-gray-400 flex-shrink-0" />
                  <span className="truncate">{s}</span>
                </button>
              ))}
            </div>
          )}
        </form>
        <AppsMenu />
        <MainMenu />
      </div>
    </header>
  );
}

export function OmniTubeIndex() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  usePageTitle(q ? `${q} — OmniTube` : "OmniTube");
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  const fetchVideos = async (query) => {
    setLoading(true);
    try {
      if (!query) {
        const { data } = await api.get("/tube/trending");
        setVideos(data.videos || []);
        setCategory(data.category || "");
      } else {
        const { data } = await api.get("/tube/search", { params: { q: query } });
        setVideos(data.videos || []);
        setCategory("");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchVideos(q); }, [q]);

  return (
    <div className="min-h-screen bg-white" data-testid="tube-page">
      <TubeHeader initial={q} onSubmit={(query) => setParams(query ? { q: query } : {})} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {!q && (
          <div className="mb-6 flex items-center gap-2 text-sm">
            <Flame size={16} className="text-red-600" />
            <span className="font-semibold">Tendances</span>
            {category && <span className="text-gray-500">· {category}</span>}
          </div>
        )}
        {q && <div className="mb-4 text-sm text-gray-500">Résultats pour « {q} »</div>}
        {loading && (
          <div className="flex items-center gap-2 text-gray-500 py-12"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
        )}
        {!loading && videos.length === 0 && (
          <div className="py-16 text-center text-gray-500">Aucune vidéo trouvée.</div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {videos.map((v, i) => <VideoCard v={v} key={`${v.id}-${i}`} />)}
        </div>
      </main>
    </div>
  );
}

export function OmniTubeWatch() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const v = params.get("v") || "";
  const t = params.get("t") || "";
  usePageTitle(t || "Lecture — OmniTube");
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const isYt = /^[A-Za-z0-9_-]{11}$/.test(v);

  useEffect(() => {
    setLoading(true);
    api.get("/tube/search", { params: { q: t || "music" } })
      .then(({ data }) => setRelated((data.videos || []).filter((x) => x.id !== v).slice(0, 10)))
      .finally(() => setLoading(false));
  }, [v, t]);

  const embedSrc = isYt ? `https://www.youtube.com/embed/${v}?autoplay=1&rel=0` : v;

  return (
    <div className="min-h-screen bg-white" data-testid="tube-watch-page">
      <TubeHeader initial={t} onSubmit={(query) => navigate(`/tube?q=${encodeURIComponent(query)}`)} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-black mb-4 inline-flex items-center gap-1" data-testid="tube-back-btn">
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="aspect-video bg-black rounded-xl overflow-hidden">
              {isYt ? (
                <iframe
                  src={embedSrc}
                  title={t}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="tube-player-iframe"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <a href={v} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300">
                    <ExternalLink size={18} /> Ouvrir la vidéo
                  </a>
                </div>
              )}
            </div>
            <h1 className="text-2xl font-black tracking-tight mt-4">{t || "Vidéo"}</h1>
            <a href={isYt ? `https://www.youtube.com/watch?v=${v}` : v} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 mt-2" data-testid="tube-source-link">
              <ExternalLink size={11} /> Voir la source YouTube
            </a>
          </div>
          <aside className="space-y-3">
            <div className="font-semibold mb-2">À suivre</div>
            {loading && <div className="text-gray-500 text-sm">Chargement…</div>}
            {related.map((r, i) => (
              <Link key={`${r.id}-${i}`} to={`/tube/watch?v=${encodeURIComponent(r.id || r.url)}&t=${encodeURIComponent(r.title)}`} className="flex gap-3 group" data-testid={`tube-related-${i}`}>
                <div className="w-40 aspect-video rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  {r.thumbnail && <img src={r.thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium line-clamp-2 group-hover:underline">{r.title}</h3>
                  <div className="text-xs text-gray-500 mt-1">{r.publisher}</div>
                  {r.duration && <div className="text-xs text-gray-400 mt-0.5">{r.duration}</div>}
                </div>
              </Link>
            ))}
          </aside>
        </div>
      </main>
    </div>
  );
}
